import { useCallback } from 'react';
import { useSignal } from '@funtech-int/rival-react';
import { useStorage, usePopupsManager } from '@funtech-int/web-toolkit';
import { useCounterOfferController, usePaymentPlace } from '@funtech-int/yango-shared';

import { useIsPaymentManagerOpened } from '@/shared/hooks/useIsPaymentManagerOpened';
import { useSearchParams } from '@/shared/hooks/useSearchParams';
import { isContentModal } from '@/shared/lib/popups';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';

const DESKTOP_ACTIVITY_EVENTS: readonly string[] = [];
const CURSOR_MOVE_THRESHOLD_PX = 50;

const useHasContentModal = () => {
    const popupsManager = usePopupsManager();
    const popups = useSignal(popupsManager.popups);

    return popups.some(isContentModal);
};

const COUNTER_OFFER_PLACES: PlusPaymentPlace[] = [
    PlusPaymentPlace.PaywallCounterOffer,
    PlusPaymentPlace.PaywallCounterOfferReference,
];

export const useCounterOffer = () => {
    const storage = useStorage();
    const hasSubscription = useHasSubscription();
    const isPaymentManagerOpened = useIsPaymentManagerOpened();
    const hasContentModal = useHasContentModal();
    const searchParams = useSearchParams();

    const page = usePaymentPage();
    const paymentPlace = usePaymentPlace({
        page,
        places: COUNTER_OFFER_PLACES,
    });

    const counterOffer = paymentPlace.selectOfferBy({
        place: PlusPaymentPlace.PaywallCounterOffer,
    });

    const baseOffer = paymentPlace.selectOfferBy({
        place: PlusPaymentPlace.PaywallCounterOfferReference,
    });

    const delayMs = Number(counterOffer?.offerTexts?.customTexts?.delayTime)
        ? Number(counterOffer?.offerTexts?.customTexts?.delayTime) * 1000
        : undefined;

    const subscribeActivity = useCallback((resetTimer: () => void) => {
        let baseX: number | null = null;
        let baseY: number | null = null;

        const onMouseMove = (event: MouseEvent) => {
            if (baseX === null || baseY === null) {
                baseX = event.clientX;
                baseY = event.clientY;

                return;
            }

            const dx = event.clientX - baseX;
            const dy = event.clientY - baseY;

            if (dx * dx + dy * dy >= CURSOR_MOVE_THRESHOLD_PX * CURSOR_MOVE_THRESHOLD_PX) {
                baseX = event.clientX;
                baseY = event.clientY;
                resetTimer();
            }
        };

        document.addEventListener('mousemove', onMouseMove);

        return () => document.removeEventListener('mousemove', onMouseMove);
    }, []);

    const { isVisible, hide } = useCounterOfferController({
        storage,
        hasSubscription,
        hasActiveModal: isPaymentManagerOpened || hasContentModal,
        hasCounterOffer: !!counterOffer,
        isShowForced: searchParams.get('counterOffer') === '1',
        activityEvents: DESKTOP_ACTIVITY_EVENTS,
        subscribeActivity,
        delayMs,
    });

    return {
        isCounterOfferVisible: isVisible,
        hideCounterOffer: hide,
        counterOffer,
        baseOffer,
        paymentPlace,
    };
};
